import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider } from 'use-intl';
import type { UserLocale } from '@leanin/shared';
import enMessages from '@/messages/en';
import { loadCatalog, type Catalog } from '@/messages/load';
import { isStaleBundleError, reloadOnceForStaleBundle } from '@/lib/stale-bundle';

// Re-provides `use-intl` for the authenticated app with the member's chosen
// locale, overriding the root provider (which stays English for public /
// auth / onboarding pages, where no member locale exists yet).
//
// `locale` and `messages` move together in one state object so the tree is
// never rendered with a locale/catalog mismatch: English shows until the
// requested catalog resolves, then both flip atomically.
export function LocaleBoundary({
  locale,
  children,
}: {
  locale: UserLocale;
  children: ReactNode;
}) {
  const [active, setActive] = useState<{ locale: UserLocale; messages: Catalog }>({
    locale: 'en',
    messages: enMessages,
  });

  useEffect(() => {
    let cancelled = false;
    loadCatalog(locale)
      .then((messages) => {
        if (!cancelled) setActive({ locale, messages });
      })
      .catch((error: unknown) => {
        // A rejected catalog load is almost always a stale-bundle chunk 404
        // after a deploy — recover with the same one-time reload the app uses
        // for route chunks (handling it here instead of leaning on the global
        // `unhandledrejection` listener, which this `.catch` now pre-empts).
        // Any other failure keeps the English catalog already in state (the
        // deliberate fallback) rather than crashing or stranding the member,
        // logged with the locale so it's diagnosable.
        if (isStaleBundleError(error)) {
          reloadOnceForStaleBundle();
          return;
        }
        console.error('[i18n] catalog load failed; staying on English', {
          locale,
          error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // Pin `now` at mount so `format.relativeTime` has a stable reference — a
  // fresh `new Date()` per render would let a timestamp drift between renders.
  const now = useMemo(() => new Date(), []);

  return (
    <IntlProvider locale={active.locale} messages={active.messages} now={now}>
      {children}
    </IntlProvider>
  );
}
